import type {ReactElement} from 'react';
import React from 'react';
import {BaseRemoveColorOptions} from '@Panels/poster-editor-item-effects-panel/components/base-remove-color-options';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromEffectsActiveItem} from '@Hooks/poster-editor/useGetKeyFromEffectsOfActiveItem';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyFromEffectsOfActiveSlideItem} from '@Hooks/poster-editor/useGetKeyFromEffectsOfActiveSlideItem';

interface RemoveColorOptionsProps {
  panelId: string;
  onClickColorFill(): void;
}

export function RemoveColorOptions({...props}: RemoveColorOptionsProps): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeItemRemoveColor = useGetKeyFromEffectsActiveItem('removeColor');
  const activeItemRemoveColorThickness = useGetKeyFromEffectsActiveItem('removeColorThickness');
  const activeItemRemoveColorValue = useGetKeyFromEffectsActiveItem('removeColorValue');

  const activeSlideUID = useGetSelectedSlideId();
  const slideItemRemoveColor = useGetKeyFromEffectsOfActiveSlideItem('removeColor');
  const slideItemRemoveColorThickness = useGetKeyFromEffectsOfActiveSlideItem('removeColorThickness');
  const slideItemRemoveColorValue = useGetKeyFromEffectsOfActiveSlideItem('removeColorValue');

  if (!activeItem) {
    return null;
  }

  if (activeItem.isSlideshow() && activeSlideUID === undefined) {
    return null;
  }

  const onToggleSlideItemRemoveColor = (): void => {
    if (activeItem.isSlideshow() && activeSlideUID) {
      void activeItem.updateSlideFromObject(activeSlideUID, {
        effects: {
          removeColor: !slideItemRemoveColor,
        },
      });
    }
  };

  const updateSlideItemTolerance = (updatedValue: number, undoable = true): void => {
    if (activeItem.isSlideshow() && activeSlideUID !== undefined) {
      void activeItem.updateSlideFromObject(
        activeSlideUID,
        {
          effects: {
            removeColorThickness: updatedValue,
          },
        },
        {
          undoable,
        }
      );
    }
  };

  const updateTolerance = (updatedValue: number, undoable = true): void => {
    void activeItem.updateFromObject(
      {
        effects: {
          removeColorThickness: updatedValue,
        },
      },
      {
        undoable,
      }
    );
  };

  const onToggleRemoveColor = (): void => {
    if (activeItem && 'effects' in activeItem) {
      void activeItem.updateFromObject({
        effects: {
          removeColor: !activeItemRemoveColor,
        },
      });
    }
  };

  const getContent = (): ReactElement | null => {
    if (activeItem.isSlideshow() && slideItemRemoveColor !== undefined && slideItemRemoveColorThickness !== undefined && slideItemRemoveColorValue) {
      return (
        <BaseRemoveColorOptions
          panelId={props.panelId}
          removeColor={slideItemRemoveColor}
          removeColorThickness={slideItemRemoveColorThickness}
          removeColorValue={slideItemRemoveColorValue}
          onToggleRemoveColor={onToggleSlideItemRemoveColor}
          updateTolerance={updateSlideItemTolerance}
          onClickColorFill={(): void => {
            props.onClickColorFill();
          }}
        />
      );
    }

    if (activeItem && 'effects' in activeItem && activeItemRemoveColor !== undefined && activeItemRemoveColorValue && activeItemRemoveColorThickness !== undefined) {
      return (
        <BaseRemoveColorOptions
          panelId={props.panelId}
          removeColor={activeItemRemoveColor}
          removeColorThickness={activeItemRemoveColorThickness}
          removeColorValue={activeItemRemoveColorValue}
          onToggleRemoveColor={onToggleRemoveColor}
          updateTolerance={updateTolerance}
          onClickColorFill={(): void => {
            props.onClickColorFill();
          }}
        />
      );
    }

    return null;
  };

  return getContent();
}
